import { useContext, useEffect, useRef, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { PaymentMethodEnum } from '@/types/common';
import { WalletButtonShell, type WalletReadyState } from '@/components/Wallets/WalletButton/WalletButtonShell';
import { ApplePayButtonPlaceholder } from '@/components/Wallets/WalletButton/ApplePayButtonPlaceholder';
import { notifyPaymentIntentClick, trackAndNotifyPaymentClick } from '@/utils/paymentClickEvents';
import { CHARGEBEE_WALLET_BUTTON_IDS } from '@/constants/chargebee';
import { chargebeeService, type ChargebeeWalletButton } from '@/services/chargebeeService';
import type { ExpressCheckoutComponentProps } from '@/types/expressCheckout';
import { useLatestRef } from '@/hooks/useLatestRef';
import { useChargebeeWalletZipGate } from '@/hooks/useChargebeeWalletZipGate';
import {
  finalizeAlreadyAuthorizedWalletPayment,
  isApplePayMerchantValidationError,
  isChargebeeUserClosedError,
} from '@/utils/chargebeeWallets';
import {
  isChargebeePaymentIntentAuthorizedError,
  isHydratedChargebeePaymentIntent,
} from '@/utils/chargebeePaymentIntent';
import { buildChargebeeApplePayRecurringRequestForSession } from '@/utils/chargebeeApplePayRecurring';
import { logError } from '@/utils/logger';
import { useTranslate } from '@/hooks/useTranslate';

/**
 * Chargebee Apple Pay (subscriptions only) — the standalone `apple-pay` handler,
 * built exactly like {@link ChargebeeGooglePayForm}: Chargebee mounts Apple's
 * button into our `WalletButtonShell`, so sizing, loading overlay, ready-pulse
 * and the  Pay mark match every other wallet. Every SDK call goes through
 * {@link chargebeeService}, and the finalize runs through `runPayment` →
 * applePayPaymentHandler's CHARGEBEE case.
 *
 * v2's Payment Components drop-in is deliberately not used: restricted to
 * `apple_pay` it still renders the site's other methods when the device can't
 * do Apple Pay, i.e. a whole second checkout inside this box. The service
 * rejects instead, which collapses the box.
 */
export const ChargebeeApplePayForm = ({ onReady, onInvalidTaxClick }: ExpressCheckoutComponentProps) => {
  const context = useContext(CheckoutContext);
  const { subscription } = context;
  const { t } = useTranslate();
  const [state, setState] = useState<WalletReadyState>('loading');
  const buttonRef = useRef<ChargebeeWalletButton | null>(null);
  const initializedRef = useRef(false);
  const contextRef = useLatestRef(context);
  const onReadyRef = useLatestRef(onReady);
  // The mount effect runs once, so `t` has to be read through a ref: its
  // identity churns as i18n resources land, and a captured copy would still be
  // the pre-load one that echoes raw keys back as the error message.
  const tRef = useLatestRef(t);
  const cbInstance = subscription?.cbInstance;
  // Chargebee validates the intent inside `setPaymentIntent`, so mounting
  // before `useChargebeePaymentIntent` has expanded the summary's `{ id }`
  // would permanently mark the wallet unavailable.
  const isPaymentIntentReady = isHydratedChargebeePaymentIntent(subscription?.paymentIntent);
  const isPaymentIntentUnavailable = subscription?.isPaymentIntentUnavailable ?? false;
  const { isBlocked: isBlockedForZipCode, blockClick } = useChargebeeWalletZipGate(onInvalidTaxClick);

  useEffect(() => {
    // Hydration is out of retries — collapse instead of spinning forever.
    if (isPaymentIntentUnavailable && !initializedRef.current) {
      setState('unavailable');
      onReadyRef.current?.(false);
    }
  }, [isPaymentIntentUnavailable, onReadyRef]);

  useEffect(() => {
    if (!cbInstance || !isPaymentIntentReady || initializedRef.current) return;
    initializedRef.current = true;
    let disposed = false;

    // Chargebee resolves the payment promise once per attempt; re-arm after a
    // dismissed sheet or a failed attempt so the button stays payable.
    const awaitNextPayment = (button: ChargebeeWalletButton) => {
      button
        .awaitPayment()
        .then(async (result) => {
          if (disposed) return;
          notifyPaymentIntentClick(contextRef.current, { paymentMethodName: PaymentMethodEnum.APPLE_PAY });
          contextRef.current.actions.setIsExpressPaymentInProcess(true);
          await contextRef.current.actions.runPayment(
            { paymentIntent: result.paymentIntent },
            PaymentMethodEnum.APPLE_PAY
          );
          if (!disposed) awaitNextPayment(button);
        })
        .catch(async (error: unknown) => {
          if (disposed) return;
          // An earlier attempt authorized this (single-use) intent but never
          // finalized, so re-opening the sheet can only ever fail while that
          // authorization stands — recover it, same as Google Pay does.
          if (isChargebeePaymentIntentAuthorizedError(error)) {
            await finalizeAlreadyAuthorizedWalletPayment(contextRef.current, PaymentMethodEnum.APPLE_PAY);
            if (!disposed) awaitNextPayment(button);
            return;
          }
          // The sheet can't open from this origin at all (unregistered payment
          // method domain on the gateway), so every retry is another dead tap:
          // say so once and take the box away.
          if (isApplePayMerchantValidationError(error)) {
            logError('Chargebee Apple Pay merchant validation failed', error);
            contextRef.current.actions.setIsExpressPaymentInProcess(false);
            contextRef.current.actions.setError(tRef.current('paymentMethodBrowserNotSupported'));
            setState('unavailable');
            onReadyRef.current?.(false);
            return;
          }
          if (!isChargebeeUserClosedError(error)) {
            logError('Chargebee Apple Pay payment failed', error);
            contextRef.current.actions.setError(tRef.current('somethingWentWrong'));
          }
          contextRef.current.actions.setIsExpressPaymentInProcess(false);
          awaitNextPayment(button);
        });
    };

    const init = async () => {
      try {
        const paymentIntent = contextRef.current.subscription?.paymentIntent;
        if (!paymentIntent) throw new Error('Missing Chargebee payment intent');
        const button = await chargebeeService.mountApplePayButton({
          selector: `#${CHARGEBEE_WALLET_BUTTON_IDS.APPLE_PAY}`,
          paymentIntent,
          // Apple decides MPAN vs DPAN at authorization, so the renewal
          // schedule has to be in the mount options — see the util.
          recurringPaymentRequest: buildChargebeeApplePayRecurringRequestForSession(contextRef.current, tRef.current),
        });
        if (disposed) return;
        buttonRef.current = button;
        setState('ready');
        onReadyRef.current?.(true);
        awaitNextPayment(button);
      } catch (error) {
        // Includes the ordinary "this browser has no Apple Pay" case — the
        // handler says so by refusing to mount.
        logError('Chargebee Apple Pay unavailable', error);
        if (disposed) return;
        setState('unavailable');
        onReadyRef.current?.(false);
      }
    };

    void init();
    return () => {
      disposed = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cbInstance, isPaymentIntentReady]);

  // A ZIP tax recalc updates the intent's amount SERVER-side under the same id,
  // so `useChargebeePaymentIntent` re-retrieves it and hands down a NEW object —
  // re-syncing on that identity alone covers the recalc, and with it the renewal
  // amount Apple quotes on the sheet.
  const paymentIntent = subscription?.paymentIntent;
  useEffect(() => {
    if (paymentIntent && buttonRef.current) {
      buttonRef.current.updatePaymentIntent(paymentIntent);
    }
  }, [paymentIntent]);

  if (state === 'unavailable') return null;

  return (
    <WalletButtonShell
      state={state}
      placeholder={<ApplePayButtonPlaceholder />}
      testId="wallet-btn-apple-chargebee"
      usePlaceholderAsVisual
    >
      <div className="relative w-full h-12 min-h-12">
        <div
          id={CHARGEBEE_WALLET_BUTTON_IDS.APPLE_PAY}
          className="w-full h-12 min-h-12 rounded-lg overflow-hidden"
          data-testid="chargebee-apple-pay-button"
          // Chargebee owns the button's click; capture it here purely for the
          // payment-click analytics every other wallet emits.
          onClickCapture={() =>
            trackAndNotifyPaymentClick(contextRef.current, { paymentMethodName: PaymentMethodEnum.APPLE_PAY })
          }
        />
        {isBlockedForZipCode && (
          <div
            data-testid="chargebee-apple-pay-zip-blocker"
            className="absolute inset-0 w-full h-full z-[2147483647]"
            onPointerDown={blockClick}
            onClick={blockClick}
          />
        )}
      </div>
    </WalletButtonShell>
  );
};
